import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { LEGAL_DOCUMENTS } from '../config/legalDocuments';
import { config } from '../config';
import { getAuthToken } from '../utils/authToken';

interface UserLegalVersions {
    tos: string | null;
    tosAcceptedAt: string | null;
    privacy: string | null;
    privacyAcceptedAt: string | null;
    aiNotice: string | null;
    aiNoticeAcceptedAt: string | null;
}

interface ProfilingConsent {
    currentVersion: string;
    userAcceptedVersion: string | null;
    acceptedAt: string | null;
    withdrawnAt: string | null;
    isActive: boolean;
}

const LegalDocumentsPage: React.FC = () => {
    const navigate = useNavigate();
    const { instance } = useMsal();
    const [userVersions, setUserVersions] = useState<UserLegalVersions | null>(null);
    const [profilingConsent, setProfilingConsent] = useState<ProfilingConsent | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadUserLegalStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadUserLegalStatus = async () => {
        try {
            const token = await getAuthToken(instance);
            if (!token) {
                setLoading(false);
                return;
            }

            const response = await fetch(`${config.apiUrl}/getLegalDocumentStatus`, {
                headers: {
                    'X-Auth-Token': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setUserVersions({
                    tos: data.userAcceptedVersions.tos,
                    tosAcceptedAt: data.userAcceptedVersions.tosAcceptedAt,
                    privacy: data.userAcceptedVersions.privacy,
                    privacyAcceptedAt: data.userAcceptedVersions.privacyAcceptedAt,
                    aiNotice: data.userAcceptedVersions.aiNotice,
                    aiNoticeAcceptedAt: data.userAcceptedVersions.aiNoticeAcceptedAt
                });
                if (data.profilingConsent) {
                    setProfilingConsent(data.profilingConsent);
                }
            }
        } catch (error) {
            console.error('Failed to load legal status:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toLocaleDateString('sk-SK', {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric'
        });
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <button onClick={() => navigate('/profile')} style={styles.backButton}>
                    &larr; Späť na profil
                </button>
                <h1 style={styles.title}>Právne informácie</h1>
                <p style={styles.subtitle}>Aktuálne verzie dokumentov platných pre používanie SmartBloček.sk</p>
            </div>

            <div style={styles.content}>
                {loading ? (
                    <div style={styles.loadingContainer}>
                        <p>Načítavam...</p>
                    </div>
                ) : (
                    <>
                        {/* Mandatory documents section */}
                        <div style={styles.sectionLabel}>Povinné dokumenty</div>

                        <LegalDocumentCard
                            title="Podmienky používania"
                            description="Pravidlá pre používanie SmartBloček"
                            version={LEGAL_DOCUMENTS.tos.currentVersion}
                            acceptedAt={userVersions?.tosAcceptedAt ? formatDate(userVersions.tosAcceptedAt) : null}
                            onOpen={() => navigate('/legal/tos')}
                        />
                        <LegalDocumentCard
                            title="Zásady ochrany osobných údajov"
                            description="Ako spracúvame vaše osobné údaje"
                            version={LEGAL_DOCUMENTS.privacy.currentVersion}
                            acceptedAt={userVersions?.privacyAcceptedAt ? formatDate(userVersions.privacyAcceptedAt) : null}
                            onOpen={() => navigate('/legal/privacy')}
                        />
                        <LegalDocumentCard
                            title="Informácie o používaní umelej inteligencie"
                            description="Ako používame AI pri spracúvaní vašich údajov"
                            version={LEGAL_DOCUMENTS.ai_notice.currentVersion}
                            acceptedAt={userVersions?.aiNoticeAcceptedAt ? formatDate(userVersions.aiNoticeAcceptedAt) : null}
                            onOpen={() => navigate('/legal/ai-notice')}
                        />

                        {/* Optional consent section */}
                        <div style={{ ...styles.sectionLabel, marginTop: '28px' }}>Voliteľné</div>

                        {profilingConsent && (
                            <div style={styles.card}>
                                <div style={styles.cardHeader}>
                                    <div style={styles.cardTitleContainer}>
                                        <h3 style={styles.cardTitle}>Profilovací súhlas</h3>
                                        <p style={styles.cardDescription}>Marketingové e-maily, sledovanie a AI prehľady</p>
                                    </div>
                                </div>
                                <div style={styles.cardMeta}>
                                    {profilingConsent.isActive ? (
                                        <>
                                            <span style={styles.statusActive}>Aktívny</span>
                                            {profilingConsent.acceptedAt && (
                                                <span style={styles.acceptedDate}>
                                                    Udelený: {formatDate(profilingConsent.acceptedAt)}
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <span style={styles.statusInactive}>Neaktívny</span>
                                    )}
                                </div>
                                <button
                                    onClick={() => navigate('/profile/marketing-emails')}
                                    style={styles.openButton}
                                >
                                    Spravovať
                                </button>
                            </div>
                        )}

                        {/* Informational documents section */}
                        <div style={{ ...styles.sectionLabel, marginTop: '28px' }}>Informačné dokumenty</div>

                        <LegalDocumentCard
                            title="Zásady cookies"
                            description="Informácie o používaní cookies a úložiska"
                            version={LEGAL_DOCUMENTS.cookies.currentVersion}
                            acceptedAt={null}
                            onOpen={() => navigate('/legal/cookies')}
                            noAcceptance
                        />
                        <LegalDocumentCard
                            title="Licenčné podmienky"
                            description="Open-source knižnice a licencie"
                            version={LEGAL_DOCUMENTS.licenses.currentVersion}
                            acceptedAt={null}
                            onOpen={() => navigate('/legal/licenses')}
                            noAcceptance
                        />
                    </>
                )}
            </div>
        </div>
    );
};

interface LegalDocumentCardProps {
    title: string;
    description: string;
    version: string;
    acceptedAt: string | null;
    onOpen: () => void;
    noAcceptance?: boolean;
}

const LegalDocumentCard: React.FC<LegalDocumentCardProps> = ({
    title,
    description,
    version,
    acceptedAt,
    onOpen,
    noAcceptance
}) => {
    return (
        <div style={styles.card}>
            <div style={styles.cardHeader}>
                <div style={styles.cardTitleContainer}>
                    <h3 style={styles.cardTitle}>{title}</h3>
                    <p style={styles.cardDescription}>{description}</p>
                </div>
            </div>
            <div style={styles.cardMeta}>
                <span style={styles.version}>Verzia {version}</span>
                {!noAcceptance && acceptedAt && (
                    <span style={styles.acceptedDate}>
                        Prijaté: {acceptedAt}
                    </span>
                )}
            </div>
            <button onClick={onOpen} style={styles.openButton}>
                Otvoriť
            </button>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        minHeight: '100vh',
        paddingBottom: '100px',
    },
    header: {
        backgroundColor: '#fff',
        padding: '20px',
    },
    backButton: {
        background: 'none',
        border: 'none',
        color: '#667eea',
        fontSize: '16px',
        cursor: 'pointer',
        padding: '8px 0',
        marginBottom: '12px',
        display: 'inline-block',
    },
    title: {
        margin: '0 0 8px 0',
        fontSize: '24px',
        fontWeight: 700,
        color: '#111827',
    },
    subtitle: {
        margin: 0,
        fontSize: '14px',
        color: '#6b7280',
    },
    content: {
        maxWidth: '600px',
        margin: '0 auto',
        padding: '20px',
    },
    sectionLabel: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#7f8c8d',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '12px',
    },
    loadingContainer: {
        textAlign: 'center',
        padding: '40px 20px',
        color: '#6b7280',
    },
    card: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: '16px',
    },
    cardTitleContainer: {
        flex: 1,
    },
    cardTitle: {
        margin: '0 0 4px 0',
        fontSize: '18px',
        fontWeight: 600,
        color: '#111827',
    },
    cardDescription: {
        margin: 0,
        fontSize: '14px',
        color: '#6b7280',
        lineHeight: '1.5',
    },
    cardMeta: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginBottom: '16px',
    },
    version: {
        fontSize: '13px',
        color: '#667eea',
        fontWeight: 500,
    },
    acceptedDate: {
        fontSize: '12px',
        color: '#9ca3af',
    },
    statusActive: {
        fontSize: '13px',
        color: '#059669',
        fontWeight: 600,
    },
    statusInactive: {
        fontSize: '13px',
        color: '#9ca3af',
        fontWeight: 500,
    },
    openButton: {
        backgroundColor: '#667eea',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        padding: '10px 20px',
        fontSize: '15px',
        fontWeight: 600,
        cursor: 'pointer',
        width: '100%',
        transition: 'background-color 0.2s',
    },
};

export default LegalDocumentsPage;
